import { Modal, Stack, Text } from "@mantine/core";
import { useState } from "react";
import { usePostProjectByProjectIdIssueboardAndIssueboardIdIssuesOnBoardAskAiMutation } from "../../../../../../openapi";
import { setMemberIdModalInRedux, setShowFailedMessageThunk } from "../../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { selectIssuesOrTasksOnBoardArgs } from "../../issueSelectors";
import {
  selectIssueAskAiFilterOnCurrentBoard,
  selectIssueAskAiHttpArgs,
  selectIssueAskAiPanelOpen,
  selectIssueAskAiPeopleOnCurrentBoard,
  selectIssueAskAiResultVisibilitySummary,
} from "../../issueAskAiSelectors";
import { setIssueAskAiPanelOpenInRedux } from "../../issueSlice";
import { IssueAskAiPanel } from "./IssueAskAiPanel";

export const IssueAskAiPanelContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const args = useAppSelector(selectIssuesOrTasksOnBoardArgs);
  const activeFilter = useAppSelector(selectIssueAskAiFilterOnCurrentBoard);
  const isPanelOpen = useAppSelector(selectIssueAskAiPanelOpen);
  const resultVisibilitySummary = useAppSelector(selectIssueAskAiResultVisibilitySummary);
  const explanationPeople = useAppSelector(selectIssueAskAiPeopleOnCurrentBoard);
  const issueAskAiHttpArgs = useAppSelector(selectIssueAskAiHttpArgs);
  const [askAi, { isLoading, reset }] = usePostProjectByProjectIdIssueboardAndIssueboardIdIssuesOnBoardAskAiMutation({
    fixedCacheKey: issueAskAiHttpArgs ?? undefined,
  });
  const [unsupportedQuestion, setUnsupportedQuestion] = useState<string | null>(null);
  const [unsupportedMessage, setUnsupportedMessage] = useState<string | null>(null);

  const close = () => {
    reset();
    dispatch(setIssueAskAiPanelOpenInRedux(false));
  };

  const submit = async (question: string) => {
    if (args.projectId == null || args.issueboardId == null) {
      return;
    }

    try {
      const response = await askAi({
        projectId: args.projectId,
        issueboardId: args.issueboardId,
        body: {
          question,
          includeIssues: args.includeIssues !== "false",
          includeTasks: args.includeTasks !== "false",
          includeOlderCompletedIssues: args.includeAllCompletedIssues === "true",
          taskOrIssue: args.taskOrIssue,
        },
      }).unwrap();

      if (response.unsupported != null) {
        setUnsupportedQuestion(question);
        setUnsupportedMessage(response.unsupported.message);
      }
    } catch (error) {
      const message =
        (error as { data?: { message?: string }; message?: string } | undefined)?.data?.message ??
        (error as { message?: string } | undefined)?.message ??
        "Kunne ikke filtrere med AI";
      dispatch(setShowFailedMessageThunk(message));
    }
  };

  return (
    <>
      <IssueAskAiPanel
        opened={isPanelOpen}
        isLoading={isLoading}
        result={
          activeFilter == null
            ? null
            : { question: activeFilter.question, explanation: activeFilter.explanation, people: explanationPeople }
        }
        resultVisibilitySummary={resultVisibilitySummary}
        submitDisabled={args.projectId == null || args.issueboardId == null}
        onClose={close}
        onSubmit={submit}
        onPersonClick={(memberId) => dispatch(setMemberIdModalInRedux(memberId))}
      />
      <Modal
        opened={unsupportedMessage != null}
        onClose={() => {
          setUnsupportedMessage(null);
          setUnsupportedQuestion(null);
        }}
        title="AI-spørsmål"
        size="md"
        yOffset={120}
        radius="md"
      >
        <Stack gap="sm">
          {unsupportedQuestion != null && (
            <div>
              <Text size="sm" c="dimmed">
                Spørsmål
              </Text>
              <Text size="sm" fw={600}>
                {unsupportedQuestion}
              </Text>
            </div>
          )}
          <Text size="sm">{unsupportedMessage ?? "Denne typen spørsmål støttes ikke ennå."}</Text>
          <Text size="sm" c="dimmed">
            Foreløpig kan AI-feltet brukes til enkle filter og semantisk søk etter relaterte saker. Sammendrag og
            analyse støttes ikke ennå.
          </Text>
        </Stack>
      </Modal>
    </>
  );
};
